import React from 'react';
import { EdgeFinancesLogo } from '../components/common/EdgeFinancesLogo';
import {
  LayoutDashboard,
  Wallet,
  HandCoins,
  FileText,
  Users2,
  Settings,
  BookUser,
  Gauge,
  Receipt,
  UserCog,
  BookOpenCheck,
  Shield,
  BookMarked,
} from 'lucide-react';
import { PRODUCT_NAME } from '../lib/tenant';
import { canAccessNav } from '../lib/permissions';
import { useCompanyProfile, companyDisplayName } from '../lib/companyProfile';
import { mediaSrc } from '../lib/mediaUrl';
import { useTranslation } from '../lib/i18n';

const sidebarItems = [
  { icon: Gauge, labelKey: 'nav.master', id: 'master' },
  { icon: LayoutDashboard, labelKey: 'nav.dashboard', id: 'dashboard' },
  { icon: BookUser, labelKey: 'nav.customers', id: 'customers' },
  { icon: Wallet, labelKey: 'nav.finance', id: 'finance' },
  { icon: HandCoins, labelKey: 'nav.loans', id: 'loans' },
  { icon: BookMarked, labelKey: 'nav.accounts', id: 'accounts' },
  { icon: FileText, labelKey: 'nav.report', id: 'report' },
  { icon: Shield, labelKey: 'nav.audit', id: 'audit' },
  { icon: BookOpenCheck, labelKey: 'nav.erp', id: 'erp' },
  { icon: Users2, labelKey: 'nav.consultancy', id: 'consultancy' },
  { icon: Receipt, labelKey: 'nav.expenses', id: 'expenses' },
  { icon: UserCog, labelKey: 'nav.employees', id: 'employees' },
  { icon: Settings, labelKey: 'nav.settings', id: 'settings' },
] as const;

interface SidebarProps {
  activeItem: string;
  setActiveItem: (id: string) => void;
}

export function Sidebar({ activeItem, setActiveItem }: SidebarProps) {
  const { profile } = useCompanyProfile();
  const { t } = useTranslation();
  const orgName = companyDisplayName(profile) || PRODUCT_NAME;
  return (
    <aside className="flex h-full w-full flex-col border-r border-zinc-800 bg-zinc-950">
      <div className="border-b border-zinc-800/80 px-3 py-4">
        <div className="flex items-center gap-2.5">
          {profile.logoUrl ? (
            <img
              src={mediaSrc(profile.logoUrl)}
              alt={orgName}
              className="h-9 w-9 shrink-0 rounded-lg border-2 border-white/25 bg-zinc-800 object-cover object-center"
            />
          ) : (
            <EdgeFinancesLogo variant="onDark" size="sm" showWordmark={false} />
          )}
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold tracking-tight text-white">{orgName}</h1>
            <p
              className="text-[10px] font-medium uppercase tracking-[0.14em]"
              style={{ color: 'var(--theme-400, #34d399)' }}
            >
              {t('nav.collections')}
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 lg:text-[10px]">
          {t('nav.workspace')}
        </p>
        <div className="space-y-0.5">
          {sidebarItems.filter((item) => canAccessNav(item.id)).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveItem(item.id)}
              className={`flex w-full items-center gap-3 rounded-md py-2.5 pl-2.5 pr-2 text-left text-[15px] font-medium transition-colors lg:gap-2.5 lg:py-2 lg:text-sm ${
                activeItem === item.id
                  ? 'border-l-2 border-l-emerald-500 bg-zinc-800 text-white'
                  : 'border-l-2 border-l-transparent text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100'
              }`}
            >
              <item.icon
                className={`h-[18px] w-[18px] shrink-0 lg:h-[17px] lg:w-[17px] ${
                  activeItem === item.id ? 'text-emerald-400' : 'text-zinc-500'
                }`}
              />
              <span className="truncate">{t(item.labelKey)}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className="mt-auto border-t border-zinc-800/80 p-2.5">
        <div className="rounded-md border border-zinc-800 bg-zinc-900/80 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-400/90 app-theme-accent-text">{t('nav.support')}</p>
          <p className="mt-0.5 text-[11px] leading-snug text-zinc-500">{t('nav.businessHours')}</p>
          <button
            type="button"
            className="mt-2 w-full rounded border border-zinc-700 bg-zinc-950 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-300 transition-colors hover:border-emerald-600/60 hover:text-white app-theme-support-btn"
          >
            {t('nav.contact')}
          </button>
        </div>
      </div>
    </aside>
  );
}
